import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../Pictures/logo.png';
import '../styles/FacultyNavbar.css'; // Import CSS file for FacultyNavbar

const FacultyNavbar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm) {
      fetch(`https://backend-main-4.onrender.com/data`)
        .then((response) => response.json())
        .then((data) => {
          const results = data.filter((student) =>
            student[1].toLowerCase().includes(searchTerm.toLowerCase()) ||
            student[0].toLowerCase().includes(searchTerm.toLowerCase())
          );
          setSearchResults(results);
          setShowSearchResult(true);
        });
    } else {
      setShowSearchResult(false);
      setSearchResults([]);
    }
  }, [searchTerm]);

  const closeSearchResult = () => {
    setShowSearchResult(false);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedStudent(null);
  };

  const handleLogout = () => {
    localStorage.setItem('isLoggedIn', 'false');
    navigate('/');
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowSearchResult(false);
  };

  return (
    <div className="navbar">
      <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <NavLink to="/" className="flex items-center">
          <img src={logo} alt="Logo" className="w-12 h-12" />
          <span className="ml-2 text-xl font-bold text-gray-800">ADMIN</span>
        </NavLink>

        {/* Navbar Links */}
        <div className="hidden md:flex space-x-6">
          <NavLink
            to="/faculty"
            className={({ isActive }) =>
              `nav-link ${isActive ? 'text-blue-500' : ''}`
            }
          >
            Home
          </NavLink>
          <NavLink
            to="/departments"
            className={({ isActive }) =>
              `nav-link ${isActive ? 'text-blue-500' : ''}`
            }
          >
            Departments
          </NavLink>
          <NavLink
            to="/saved"
            className={({ isActive }) =>
              `nav-link ${isActive ? 'text-blue-500' : ''}`
            }
          >
            Saved
          </NavLink>
          <NavLink
            to="/students-drive-details"
            className={({ isActive }) =>
              `nav-link ${isActive ? 'text-blue-500' : ''}`
            }
          >
            Students Drive Details
          </NavLink>
          <div className="relative">
            <button
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              className="nav-link"
            >
              Settings
            </button>
            {showSettingsDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg">
                <NavLink
                  to="/add-trainers"
                  className="block px-4 py-2 text-gray-800 hover:bg-gray-200"
                >
                  Add Trainers
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-200"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <form className="hidden md:flex items-center space-x-2" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            placeholder="Search"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className="block md:hidden text-gray-800 focus:outline-none"
          onClick={() => {
            const menu = document.getElementById('mobile-menu');
            menu.classList.toggle('hidden');
          }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </nav>

      {/* Search Result Popup */}
      {showSearchResult && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeSearchResult}
        >
          <div
            className="search-result-popup p-6 rounded-lg shadow-lg w-96"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSearchResult}
              className="absolute top-2 right-2 close-button"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="text-xl font-semibold mb-4">Search Results</h3>
            {searchResults.length > 0 ? (
              <ul>
                {searchResults.map((result, index) => (
                  <li
                    key={index}
                    className="result-item cursor-pointer"
                    onClick={() => handleStudentClick(result)}
                  >
                    <p className="font-semibold">{result[1]}</p>
                    <p className="text-gray-600">Roll Number: {result[0]}</p>
                    <p className="text-gray-600">Department: {result[5]}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">No results found</p>
            )}
          </div>
        </div>
      )}

      {/* Student Details Popup */}
      {selectedStudent && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeSearchResult}
        >
          <div
            className="student-details-popup p-6 rounded-lg shadow-lg w-96"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSearchResult}
              className="absolute top-2 right-2 close-button"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="text-xl font-semibold mb-4">Student Details</h3>
            <p className="font-semibold">{selectedStudent[1]}</p>
            <p className="text-gray-600">Roll Number: {selectedStudent[0]}</p>
            <p className="text-gray-600">Department: {selectedStudent[5]}</p>
            <p className="text-gray-600">Class: {selectedStudent[4]}</p>
            <p className="text-gray-600">Email: {selectedStudent[2]}</p>
            <p className="text-gray-600">Phone: {selectedStudent[3]}</p>
            <p className="text-gray-600">GitHub: {selectedStudent[13]}</p>
            <p className="text-gray-600">Portfolio: {selectedStudent[14]}</p>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      <div id="mobile-menu" className="hidden md:hidden bg-white shadow-md">
        <NavLink
          to="/faculty"
          className={({ isActive }) =>
            `block px-4 py-2 text-gray-600 hover:bg-blue-100 ${isActive ? 'bg-blue-100' : ''}`
          }
        >
          Home
        </NavLink>
        <NavLink
          to="/departments"
          className={({ isActive }) =>
            `block px-4 py-2 text-gray-600 hover:bg-blue-100 ${isActive ? 'bg-blue-100' : ''}`
          }
        >
          Departments
        </NavLink>
        <NavLink
          to="/saved"
          className={({ isActive }) =>
            `block px-4 py-2 text-gray-600 hover:bg-blue-100 ${isActive ? 'bg-blue-100' : ''}`
          }
        >
          Saved
        </NavLink>
        <NavLink
          to="/students-drive-details"
          className={({ isActive }) =>
            `block px-4 py-2 text-gray-600 hover:bg-blue-100 ${isActive ? 'bg-blue-100' : ''}`
          }
        >
          Students Drive Details
        </NavLink>
        <div className="relative">
          <button
            onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            className="block px-4 py-2 text-gray-600 hover:bg-blue-100"
          >
            Settings
          </button>
          {showSettingsDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg">
              <NavLink
                to="/add-trainers"
                className="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
                Add Trainers
              </NavLink>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacultyNavbar;